import React, { useCallback, useState } from 'react';

import PropTypes from 'prop-types';

import clsx from 'clsx';

import {
    Collapsible,
    Glyphicon,
    Table,
    Text,
} from 'reaxl';

/**
 *  The OperatingHours component is a display component that will take an array containing objects of with label, open, and close properties
 */

const daysWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const usersDay = daysWeek[new Date().getDay()];

const formatOperatingHours = (open = '', close = '') => (open.toLowerCase() === 'closed' ? 'Closed' : [open, close].join(' - '));

// Only generate text content of the label
const renderLabelText = (days, isDealerOpen) => {
    const { close } = days.find((day) => day.label === usersDay);
    return (
        isDealerOpen ? (
            <span>
                <Text
                    color="success"
                >
                    Open
                </Text>
                <Text
                    size={300}
                    className="margin-horizontal-1"
                >
                    &#183;
                </Text>
                <Text weight="normal">{`Closes at ${close}`}</Text>
            </span>
        ) : (
            <Text
                color="subdued-lightest"
            >
                Closed
            </Text>
        )
    );
};

const renderRow = ({ label, open, close }) => (
    <tr
        key={label}
        className={clsx({ 'text-bold': usersDay === label })}
    >
        <td>{label}</td>
        <td>{formatOperatingHours(open, close)}</td>
    </tr>
);

const renderTableBody = (days) => days.map(renderRow);

function OperatingHours({ hours, isDealerOpen = false, ...rest }) {

    const [isOpen, setIsOpen] = useState(false);

    const onToggle = useCallback(() => setIsOpen((prevState) => !prevState), []);

    return !!hours && (
        <div
            data-cmp="operatingHours"
            {...rest}
        >
            <Collapsible
                id="toggled-hours"
                key="toggled-hours"
                toggleHeader={(
                    <Text
                        size={400}
                        weight="bold"
                        className="display-flex gap-2 align-items-center"
                    >
                        {renderLabelText(hours, isDealerOpen)}
                        <Glyphicon
                            className="text-size-500"
                            glyph={isOpen ? 'menu-up' : 'menu-down'}
                        />
                        <span className="sr-only">{`(click to ${isOpen ? 'hide' : 'show'} hours)`}</span>
                    </Text>
                )}
                initialCollapse
                onToggle={onToggle}
            >
                {/* Use a div as a wrapper so that Collapse from react-bootstrap can add classes/styles to it without breaking the Table style */}
                <div>
                    <Table className="margin-bottom-0 margin-top-2 table-unstyled">
                        <tbody>
                            {renderTableBody(hours)}
                        </tbody>
                    </Table>
                </div>
            </Collapsible>
        </div>
    );
}

OperatingHours.propTypes = {
    /**
     * The className to apply to the root node
     */
    className: PropTypes.string,
    /**
     * The hours array houses the objects required for label, open, and close strings
     */
    hours: PropTypes.array,
    /**
     * The isDealerOpen boolean determines if dealership is open or not for
     */
    isDealerOpen: PropTypes.bool,
};

export default OperatingHours;
